"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Calendar as CalendarIcon, Clock, UserCheck, CreditCard, ShieldCheck, CircleCheck as CheckCircle2, TriangleAlert as AlertCircle, Phone, Mail, MapPin, CircleQuestionMark as HelpCircle, Loader as Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const steps = [{
  number: "01",
  title: "Selección de área y especialista",
  description: "Indique la materia (Gobierno, Empresa o Legal) y el consultor senior de su preferencia.",
  icon: UserCheck
}, {
  number: "02",
  title: "Propuesta de fecha y modalidad",
  description: "Elija el día y horario conveniente, sea presencial en oficina o mediante videollamada.",
  icon: CalendarIcon
}, {
  number: "03",
  title: "Confirmación y seguimiento",
  description: "Nuestro equipo directivo valida la agenda y le remite el enlace de acceso o pase de visita.",
  icon: CreditCard
}];
export default function Appointment() {
  const shouldReduceMotion = useReducedMotion();
  const [formDef, setFormDef] = useState<any>(null);
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "error">("loading");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);

  // Form values keyed by field ID (or field key as mapped)
  const [formData, setFormData] = useState<Record<string, string>>({
    area: "",
    specialist: "",
    modality: "videollamada",
    date: "",
    time: "",
    description: "",
    full_name: "",
    email: "",
    phone: ""
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<{
    html: string;
    reference?: string | number;
  } | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [honeypotVal, setHoneypotVal] = useState<string>("");
  const checkoutMounted = useRef(false);

  // Load Form definition
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // In standalone environment without WVC runtime
      setLoadStatus("ready");
      setFetchTimestamp(Date.now());
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/consultation_booking_form").then((data: any) => {
      if (!isMounted) return;
      setFormDef(data);
      setFetchTimestamp(Date.now());
      setLoadStatus("ready");
    }).catch((err: any) => {
      if (err?.status === 404) {
        // Recovery attempt per WVC Forms guidelines
        client.pluginRequest("wvc-forms/v1/forms").then((listRes: any) => {
          if (!isMounted) return;
          const items = listRes?.items || [];
          const matched = items.find((f: any) => f.key === "consultation_booking_form") || (items.length === 1 ? items[0] : null) || items.find((f: any) => f.name?.toLowerCase().includes("cita") || f.name?.toLowerCase().includes("consulta"));
          if (matched) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`).then((recoveredForm: any) => {
              if (!isMounted) return;
              setFormDef(recoveredForm);
              setFetchTimestamp(Date.now());
              setLoadStatus("ready");
            });
          } else {
            setLoadStatus("ready"); // Render fallback accessible structure
          }
        }).catch(() => {
          if (isMounted) setLoadStatus("ready");
        });
      } else {
        if (isMounted) setLoadStatus("ready");
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const mountCheckout = useCallback((node: HTMLDivElement | null) => {
    if (!node || checkoutMounted.current || !paymentData?.client) return;
    const pay = getWvcClient()?.integrations?.payments;
    if (!pay) {
      node.textContent = "La pasarela segura se activará con su confirmación de agenda.";
      return;
    }
    checkoutMounted.current = true;
    pay.checkout(paymentData.client, {
      mount: node,
      onSuccess: () => {},
      onError: (err: any) => {
        setGeneralError(err?.message || "Error al procesar el pago.");
      }
    });
  }, [paymentData]);
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError("");
    setFieldErrors({});

    // Client-side minimum validations
    const errors: Record<string, string> = {};
    if (!formData.full_name.trim()) {
      errors.full_name = "Por favor ingrese su nombre completo.";
    }
    if (!formData.email.trim()) {
      errors.email = "Por favor ingrese su correo electrónico.";
    } else if (!formData.email.includes("@")) {
      errors.email = "El correo electrónico no es válido.";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Por favor ingrese su número telefónico.";
    }
    if (!formData.date) {
      errors.date = "Seleccione la fecha preferida para la sesión.";
    }
    if (!formData.time) {
      errors.time = "Seleccione el horario deseado.";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Minimum delay enforcement per spam requirements
    const minSeconds = formDef?.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsedSeconds + 0.5) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();

    // Map input values to field IDs if schema is present
    const valuesToSend: Record<string, any> = {};
    if (formDef?.fields && Array.isArray(formDef.fields)) {
      formDef.fields.forEach((f: any) => {
        const keyMatch = f.key || "";
        if (keyMatch.includes("area") || keyMatch.includes("servicio")) {
          valuesToSend[f.id] = formData.area;
        } else if (keyMatch.includes("profesional") || keyMatch.includes("especialista")) {
          valuesToSend[f.id] = formData.specialist;
        } else if (keyMatch.includes("modalidad") || keyMatch.includes("tipo")) {
          valuesToSend[f.id] = formData.modality;
        } else if (keyMatch.includes("fecha") || keyMatch.includes("date")) {
          valuesToSend[f.id] = formData.date;
        } else if (keyMatch.includes("hora") || keyMatch.includes("time")) {
          valuesToSend[f.id] = formData.time;
        } else if (keyMatch.includes("asunto") || keyMatch.includes("mensaje") || keyMatch.includes("descripcion")) {
          valuesToSend[f.id] = formData.description;
        } else if (keyMatch.includes("nombre") || keyMatch.includes("name")) {
          valuesToSend[f.id] = formData.full_name;
        } else if (keyMatch.includes("email") || keyMatch.includes("correo")) {
          valuesToSend[f.id] = formData.email;
        } else if (keyMatch.includes("tel") || keyMatch.includes("phone")) {
          valuesToSend[f.id] = formData.phone;
        } else {
          // Send field default if available
          if (f.default) valuesToSend[f.id] = f.default;
        }
      });
    } else {
      // Direct field keys when form definition is generic
      Object.assign(valuesToSend, formData);
    }
    const payload: any = {
      values: valuesToSend,
      token: formDef?.spam?.token || "standard_token",
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef?.spam?.honeypot?.field) {
      payload.hp = {
        [formDef.spam.honeypot.field]: honeypotVal
      };
    }
    if (client?.pluginRequest) {
      try {
        const targetKey = formDef?.key || "consultation_booking_form";
        const res = await client.pluginRequest(`wvc-forms/v1/forms/${targetKey}/submit`, {
          method: "POST",
          data: payload
        });
        if (res.success) {
          if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
            window.location.assign(res.confirmation.url);
            return;
          }
          setConfirmation({
            html: res.confirmation?.message || "<p>Su solicitud ha sido recibida satisfactoriamente. Un asesor de ASV Consultores le contactará en breve.</p>",
            reference: res.entry?.reference
          });
          if (res.payment) {
            setPaymentData(res.payment);
          }
        } else if (res.code === "frms_validation_failed" && res.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(res.errors).forEach(([fieldId, errObj]: any) => {
            mappedErrors[fieldId] = errObj.message;
          });
          setFieldErrors(mappedErrors);
          setGeneralError(res.message || "Por favor revise los campos señalados.");
        } else {
          setGeneralError(res.message || "No fue posible registrar la cita. Intente de nuevo.");
        }
      } catch (err: any) {
        setGeneralError(err?.message || "Ocurrió una eventualidad de conexión. Por favor reintente en unos instantes.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Standalone simulation fallback
      setTimeout(() => {
        setIsSubmitting(false);
        setConfirmation({
          html: "<p>Su cita ha sido agendada con éxito. Nuestro equipo revisará los detalles y se comunicará vía telefónica o correo electrónico para confirmar la sesión.</p>",
          reference: 4892
        });
      }, 750);
    }
  };
  return <section id="solicitar-cita" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-muted text-foreground text-xs font-serif font-bold uppercase tracking-widest border border-border">
            <span>Agenda Institucional</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground uppercase">
            Solicitar cita con un especialista
          </h2>
          {/* ASV Gold Hairline */}
          <div className="w-12 h-[2px] bg-accent mt-4 mb-5" />
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Reserve una sesión estratégica orientada a sus requerimientos
            específicos en materia gubernamental, corporativa o jurídica.
            Recibirá confirmación puntual y directa de nuestro equipo.
          </p>
        </div>

        {/* 5 / 7 Asymmetric Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column (5 cols): Workflow & Firm Details */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 space-y-8">
            {/* Step-by-step Process Dossier */}
            <div className="rounded-lg border border-border bg-card p-6 sm:p-8 shadow-xs">
              <h3 className="font-serif text-lg font-bold tracking-wide uppercase text-foreground mb-6">
                Protocolo de atención
              </h3>
              <div className="space-y-6">
                {steps.map((step, index) => {
                const StepIcon = step.icon;
                return <div key={step.number} data-index={index} className="flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-background border border-border text-foreground font-serif text-sm font-bold">
                        {step.number}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <StepIcon className="size-4 text-foreground/80" />
                          <h4 className="font-serif text-sm font-bold uppercase tracking-tight text-foreground">
                            {step.title}
                          </h4>
                        </div>
                        <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-8 pt-6 border-t border-border flex items-center gap-3 text-xs text-muted-foreground">
                <ShieldCheck className="size-5 text-foreground shrink-0" />
                <span>
                  Atención supervisada por el Director General L.C.P. Jesús Adán
                  Avilez Soto y socios titulares.
                </span>
              </div>
            </div>

            {/* Direct Office Contacts */}
            <div className="rounded-lg border border-border bg-card p-6 sm:p-8 space-y-4 shadow-xs">
              <h4 className="font-serif text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Sede Central y Asistencia
              </h4>
              <div className="space-y-3 font-sans text-sm text-foreground">
                <div className="flex items-start gap-3">
                  <MapPin className="size-4 text-foreground shrink-0 mt-0.5" />
                  <span className="text-muted-foreground leading-snug">
                    Av. Rosa Morada 1360, Fracc. Praderas, Los Mochis, Ahome,
                    Sinaloa, México
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="size-4 text-foreground shrink-0" />
                  <a href="tel:6672505982" className="hover:underline text-foreground font-medium">
                    667 250 5982
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-foreground shrink-0" />
                  <a href="mailto:javilez@avsconsultores.org" className="hover:underline text-foreground font-medium">
                    javilez@avsconsultores.org
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="size-4 text-foreground shrink-0" />
                  <span className="text-muted-foreground">
                    Lunes a Viernes: 9:00 - 18:00
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column (7 cols): Interactive Booking Engine */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {} : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-sm rounded-lg p-2 sm:p-4">
              <CardContent className="p-4 sm:p-6">
                {confirmation ? <div className="py-10 text-center space-y-6">
                    <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-muted text-foreground border border-border">
                      <CheckCircle2 className="size-8" />
                    </div>
                    <div className="space-y-2 max-w-md mx-auto">
                      <h3 className="font-serif text-2xl font-bold uppercase text-foreground">
                        Cita Registrada
                      </h3>
                      {confirmation.reference && <p className="font-mono text-xs text-muted-foreground uppercase">
                          Folio de registro: #{confirmation.reference}
                        </p>}
                      <div className="font-sans text-sm text-muted-foreground leading-relaxed pt-2" dangerouslySetInnerHTML={{
                    __html: confirmation.html
                  }} />
                    </div>

                    {paymentData && <div className="mt-6 pt-6 border-t border-border text-left">
                        <div className="rounded-md border border-border bg-background p-4 mb-4">
                          <p className="font-serif text-xs font-bold uppercase text-foreground mb-1">
                            Liquidación de honorarios
                          </p>
                          <p className="font-sans text-xs text-muted-foreground mb-3">
                            Monto de la consulta: ${paymentData.amount}{" "}
                            {paymentData.currency}
                          </p>
                          <div ref={mountCheckout} className="min-h-[48px]" />
                        </div>
                      </div>}

                    <div className="pt-4">
                      <Button variant="outline" onClick={() => {
                    setConfirmation(null);
                    setPaymentData(null);
                    setFormData({
                      area: "",
                      specialist: "",
                      modality: "videollamada",
                      date: "",
                      time: "",
                      description: "",
                      full_name: "",
                      email: "",
                      phone: ""
                    });
                  }} className="font-serif uppercase tracking-wider text-xs font-semibold">
                        Agendar otra sesión
                      </Button>
                    </div>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot hidden input */}
                    {formDef?.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] -top-[9999px] opacity-0" aria-hidden="true" />}

                    {generalError && <div className="flex items-center gap-3 p-3 text-xs sm:text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                        <AlertCircle className="size-4 shrink-0" />
                        <span>{generalError}</span>
                      </div>}

                    <div className="space-y-4">
                      <div className="border-b border-border pb-2">
                        <h4 className="font-serif text-xs font-bold uppercase tracking-widest text-foreground">
                          Detalles de la asesoría
                        </h4>
                      </div>

                      {/* Area / Service & Specialist */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Área o servicio
                          </Label>
                          <Select value={formData.area} onValueChange={val => handleInputChange("area", val)}>
                            <SelectTrigger className="w-full bg-background border-input text-foreground h-11">
                              <SelectValue placeholder="Seleccione un área" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gobierno">
                                Gobierno y Contabilidad CONAC
                              </SelectItem>
                              <SelectItem value="empresa">
                                Empresas y Gasolineras
                              </SelectItem>
                              <SelectItem value="legal">
                                Jurídico (Fiscal, Admin., Agrario)
                              </SelectItem>
                              <SelectItem value="capacitacion">
                                Programas de Capacitación
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {fieldErrors.area && <p className="text-xs text-destructive">
                              {fieldErrors.area}
                            </p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Profesional de preferencia
                          </Label>
                          <Select value={formData.specialist} onValueChange={val => handleInputChange("specialist", val)}>
                            <SelectTrigger className="w-full bg-background border-input text-foreground h-11">
                              <SelectValue placeholder="Asignación preferente" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="jesus_avilez">
                                L.C.P. Jesús Adán Avilez Soto (Dir. General)
                              </SelectItem>
                              <SelectItem value="socio_fiscal">
                                Especialista Fiscal y Corporativo
                              </SelectItem>
                              <SelectItem value="socio_gubernamental">
                                Consultor en Armonización CONAC
                              </SelectItem>
                              <SelectItem value="socio_juridico">
                                Abogado Senior en Derecho Administrativo
                              </SelectItem>
                              <SelectItem value="sin_preferencia">
                                Cualquier especialista disponible
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Modality, Date and Time */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                          <Label className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Modalidad
                          </Label>
                          <Select value={formData.modality} onValueChange={val => handleInputChange("modality", val)}>
                            <SelectTrigger className="w-full bg-background border-input text-foreground h-11">
                              <SelectValue placeholder="Modalidad" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="videollamada">
                                Videollamada
                              </SelectItem>
                              <SelectItem value="presencial">
                                Presencial en oficina
                              </SelectItem>
                              <SelectItem value="telefonica">
                                Llamada telefónica
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="booking-date" className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Fecha preferida *
                          </Label>
                          <Input id="booking-date" type="date" min={new Date().toISOString().split("T")[0]} value={formData.date} onChange={e => handleInputChange("date", e.target.value)} className="bg-background border-input text-foreground h-11 px-3" />
                          {fieldErrors.date && <p className="text-xs text-destructive">
                              {fieldErrors.date}
                            </p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Hora preferida *
                          </Label>
                          <Select value={formData.time} onValueChange={val => handleInputChange("time", val)}>
                            <SelectTrigger className="w-full bg-background border-input text-foreground h-11">
                              <SelectValue placeholder="Horario" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="09:00">09:00 hrs</SelectItem>
                              <SelectItem value="10:30">10:30 hrs</SelectItem>
                              <SelectItem value="12:00">12:00 hrs</SelectItem>
                              <SelectItem value="14:00">14:00 hrs</SelectItem>
                              <SelectItem value="16:00">16:00 hrs</SelectItem>
                              <SelectItem value="17:30">17:30 hrs</SelectItem>
                            </SelectContent>
                          </Select>
                          {fieldErrors.time && <p className="text-xs text-destructive">
                              {fieldErrors.time}
                            </p>}
                        </div>
                      </div>

                      {/* Brief description */}
                      <div className="space-y-1.5">
                        <Label htmlFor="booking-description" className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                          Breve descripción del asunto
                        </Label>
                        <Textarea id="booking-description" rows={3} value={formData.description} onChange={e => handleInputChange("description", e.target.value)} placeholder="Indique los antecedentes primarios, entidad involucrada o dudas fiscales/jurídicas a tratar..." className="bg-background border-input text-foreground px-3 py-2 text-sm" />
                      </div>
                    </div>

                    {/* Personal contact data */}
                    <div className="space-y-4 pt-2">
                      <div className="border-b border-border pb-2">
                        <h4 className="font-serif text-xs font-bold uppercase tracking-widest text-foreground">
                          Datos de contacto
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="booking-name" className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Nombre completo *
                          </Label>
                          <Input id="booking-name" type="text" value={formData.full_name} onChange={e => handleInputChange("full_name", e.target.value)} placeholder="Ej. Lic. Roberto Gómez" className="bg-background border-input text-foreground h-11 px-3" />
                          {fieldErrors.full_name && <p className="text-xs text-destructive">
                              {fieldErrors.full_name}
                            </p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="booking-email" className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Correo electrónico *
                          </Label>
                          <Input id="booking-email" type="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} placeholder="contacto@empresa.mx" className="bg-background border-input text-foreground h-11 px-3" />
                          {fieldErrors.email && <p className="text-xs text-destructive">
                              {fieldErrors.email}
                            </p>}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="booking-phone" className="font-serif text-xs uppercase font-bold tracking-wider text-foreground">
                            Teléfono celular o directo *
                          </Label>
                          <Input id="booking-phone" type="tel" value={formData.phone} onChange={e => handleInputChange("phone", e.target.value)} placeholder="668 123 4567" className="bg-background border-input text-foreground h-11 px-3" />
                          {fieldErrors.phone && <p className="text-xs text-destructive">
                              {fieldErrors.phone}
                            </p>}
                        </div>
                      </div>
                    </div>

                    {/* Submit Bar */}
                    <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <HelpCircle className="size-4 text-foreground/70 shrink-0" />
                        <span>
                          Sin cobro por solicitud inicial. Confirmación sujeta a
                          agenda ejecutiva.
                        </span>
                      </div>

                      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto font-serif uppercase tracking-widest text-xs font-semibold px-8 py-3 h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                        {isSubmitting ? <span className="flex items-center gap-2">
                            <Loader2 className="size-4 animate-spin" />
                            Procesando agenda...
                          </span> : "Confirmar solicitud de cita"}
                      </Button>
                    </div>
                  </form>}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}